import { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CheckCircle, Frown, Star, Loader2, Sparkles, ThumbsUp, ThumbsDown } from "lucide-react";
import { API_URL } from "../config/api";

// Six segments, Kaizen-style — order matters: it's the order in the email.
const CATEGORIES = [
  {
    key: "customer_support",
    title: "Customer Support",
    blurb: "Professionalism, availability, and helpfulness of our team.",
  },
  {
    key: "customs_clearance",
    title: "Customs Clearance",
    blurb: "Speed, accuracy, and efficiency of customs handling.",
  },
  {
    key: "timely_responses",
    title: "Timely Responses",
    blurb: "Response times and communication speed.",
  },
  {
    key: "track_visibility",
    title: "Track & Trace Visibility",
    blurb: "Shipment visibility and tracking information.",
  },
  {
    key: "complaint_resolution",
    title: "Complaint Resolution",
    blurb: "Handling and resolution of any issues that arose.",
  },
  {
    key: "overall_experience",
    title: "Overall Experience",
    blurb: "Your overall experience working with Europacific.",
  },
];

const RATING_LABELS = {
  1: "Poor",
  2: "Fair",
  3: "Good",
  4: "Very Good",
  5: "Excellent",
};

function StarRow({ value, onChange, testIdPrefix }) {
  // 5 buttons, click to set, hover to preview. Star colours: amber on
  // active, slate-300 on idle. Click on the SAME star clears it (toggle).
  const [hover, setHover] = useState(0);
  const display = hover || value || 0;
  return (
    <div
      className="flex items-center gap-1 sm:gap-2"
      onMouseLeave={() => setHover(0)}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= display;
        return (
          <button
            key={n}
            type="button"
            data-testid={`${testIdPrefix}-star-${n}`}
            onMouseEnter={() => setHover(n)}
            onClick={() => onChange(value === n ? null : n)}
            aria-label={`${RATING_LABELS[n]} (${n} star${n > 1 ? "s" : ""})`}
            className="group relative p-1.5 sm:p-2 rounded-md transition-transform active:scale-95"
          >
            <Star
              className={`h-7 w-7 sm:h-8 sm:w-8 transition-colors ${
                filled
                  ? "text-amber-400 fill-amber-400"
                  : "text-slate-300 group-hover:text-amber-300"
              }`}
              strokeWidth={1.5}
            />
          </button>
        );
      })}
      <span
        data-testid={`${testIdPrefix}-label`}
        className={`ml-2 text-xs sm:text-sm font-medium min-w-[70px] ${
          value ? "text-amber-700" : "text-slate-400"
        }`}
      >
        {value ? RATING_LABELS[value] : "Tap to rate"}
      </span>
    </div>
  );
}

export default function SatisfactionSurveyPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const prefillCategory = searchParams.get("category");
  const prefillScore = parseInt(searchParams.get("score") || "0", 10);

  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [autoSubmitted, setAutoSubmitted] = useState(false);

  // Per-category ratings
  const [ratings, setRatings] = useState(() => {
    const init = {};
    CATEGORIES.forEach((c) => (init[c.key] = null));
    return init;
  });
  const [improvement, setImprovement] = useState("");
  const [wouldRecommend, setWouldRecommend] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSurvey(); }, [token]);

  // One-click flow from the email: if the URL carries category + score, we
  // POST the rating immediately and show a thank-you screen. The customer
  // never sees the form. They can still re-open the link to add more.
  useEffect(() => {
    const validCategory = prefillCategory && CATEGORIES.some((c) => c.key === prefillCategory);
    const validScore = prefillScore >= 1 && prefillScore <= 5;
    if (!validCategory || !validScore) return;
    if (!survey || autoSubmitted || submitted || alreadyCompleted) return;

    // If this category is ALREADY locked from a prior click, don't overwrite.
    // Show the existing rating as confirmation.
    const priorForThisCategory = survey.categories?.[prefillCategory];
    if (priorForThisCategory) {
      setAutoSubmitted(true);
      setSubmitted(true);
      return;
    }

    setRatings((r) => ({ ...r, [prefillCategory]: prefillScore }));
    setAutoSubmitted(true);

    (async () => {
      setSubmitting(true);
      try {
        const payload = { [prefillCategory]: prefillScore };
        await axios.post(`${API_URL}/service-satisfaction/submit/${token}`, payload);
        setSubmitted(true);
      } catch (error) {
        // If submit fails (e.g. already completed), just show the form so
        // the customer can try again.
        setAutoSubmitted(false);
        toast.error(error.response?.data?.detail || "Couldn't save your rating — please try the form below.");
      } finally {
        setSubmitting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey, prefillCategory, prefillScore]);

  const fetchSurvey = async () => {
    try {
      const response = await axios.get(`${API_URL}/service-satisfaction/survey/${token}`);
      const data = response.data || {};
      if (data.status === "already_completed") {
        setAlreadyCompleted(true);
      } else {
        setSurvey(data);
        // Hydrate any ratings already on the row (e.g. an earlier one-click)
        if (data.categories && typeof data.categories === "object") {
          setRatings((prev) => {
            const merged = { ...prev };
            for (const k of Object.keys(data.categories)) {
              const v = data.categories[k];
              if (typeof v === "number" && v >= 1 && v <= 5) merged[k] = v;
            }
            return merged;
          });
        }
        if (data.improvement_suggestion) setImprovement(data.improvement_suggestion);
        if (typeof data.would_recommend === "boolean") setWouldRecommend(data.would_recommend);
      }
    } catch (error) {
      console.error("Failed to fetch survey:", error);
      toast.error("Survey not found or has expired");
    } finally {
      setLoading(false);
    }
  };

  const filledCount = useMemo(
    () => Object.values(ratings).filter(Boolean).length,
    [ratings]
  );
  const averageRating = useMemo(() => {
    const vals = Object.values(ratings).filter(Boolean);
    if (!vals.length) return null;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  }, [ratings]);

  const handleSubmit = async () => {
    const hasAnyRating = filledCount > 0;
    const hasText = improvement.trim().length > 0;
    const hasNps = wouldRecommend !== null;

    if (!hasAnyRating && !hasText && !hasNps) {
      toast.error("Please rate at least one category, or leave a comment.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...ratings,
        improvement_suggestion: improvement.trim() || null,
        would_recommend: wouldRecommend,
      };
      await axios.post(`${API_URL}/service-satisfaction/submit/${token}`, payload);
      setSubmitted(true);
      toast.success("Thank you for your feedback!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit survey");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || (autoSubmitted && submitting)) {
    return (
      <div data-testid="survey-loading" className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-amber-500 mx-auto" />
          <p className="text-slate-600 mt-4 text-sm">
            {autoSubmitted ? "Saving your rating…" : "Loading survey…"}
          </p>
        </div>
      </div>
    );
  }

  if (alreadyCompleted || submitted) {
    const lockedScore =
      (autoSubmitted && survey?.categories?.[prefillCategory]) ||
      (autoSubmitted ? prefillScore : null);
    const lockedCategory = autoSubmitted
      ? CATEGORIES.find((c) => c.key === prefillCategory)
      : null;
    const isAlreadyLocked =
      autoSubmitted && survey?.categories?.[prefillCategory] &&
      survey.categories[prefillCategory] !== prefillScore;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50 flex items-center justify-center p-4">
        <Card data-testid="survey-thank-you" className="max-w-lg w-full shadow-xl">
          <CardContent className="pt-10 pb-10 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-3">Thank you!</h2>
            {lockedScore ? (
              <>
                <p className="text-slate-600">
                  Your <strong>{RATING_LABELS[lockedScore]}</strong> ({lockedScore}★) rating for{" "}
                  <strong>{lockedCategory?.title}</strong> {isAlreadyLocked ? "is already recorded" : "has been recorded"}.
                </p>
                <div className="flex justify-center mt-2 mb-4">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className={`h-7 w-7 ${
                        n <= lockedScore ? "text-amber-400 fill-amber-400" : "text-slate-200"
                      }`}
                      strokeWidth={1.5}
                    />
                  ))}
                </div>
                {isAlreadyLocked && (
                  <p className="text-xs text-slate-500 mb-4 italic">
                    Each category is locked after the first click to keep the data clean — your original score stays.
                  </p>
                )}
                <p className="text-sm text-slate-500 mt-4 mb-6">
                  You can safely close this tab — or add more feedback below.
                </p>
                <Button
                  data-testid="survey-add-more-btn"
                  onClick={() => {
                    setSubmitted(false);
                    setAutoSubmitted(false);
                    setAlreadyCompleted(false);
                  }}
                  className="bg-[#1a365d] hover:bg-[#0f1e3a]"
                >
                  Add more feedback
                </Button>
              </>
            ) : (
              <p className="text-slate-600">
                {submitted
                  ? "Your feedback has been submitted. Every score, every comment helps us improve — that's Kaizen in action."
                  : "You have already submitted your feedback for this shipment. Thank you for helping us improve!"}
              </p>
            )}
            <div className="mt-8 pt-6 border-t">
              <p className="text-sm text-slate-500">Europacific Logistics d.o.o.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50 flex items-center justify-center p-4">
        <Card data-testid="survey-not-found" className="max-w-lg w-full shadow-xl">
          <CardContent className="pt-10 pb-10 text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="h-10 w-10 text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-3">
              This feedback link has expired
            </h2>
            <p className="text-slate-600 mb-4">
              The survey may be from an older email or has already been processed. Each Europacific feedback link can only be used during the shipment's feedback window.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              If you'd like to share feedback on a recent shipment, please reply to your latest Europacific email or contact us directly.
            </p>
            <div className="pt-6 border-t">
              <p className="text-sm text-slate-500 mb-1">Europacific Logistics d.o.o.</p>
              <p className="text-xs text-slate-400">
                <a href="mailto:info@europacific.com" className="text-blue-600 hover:underline">
                  info@europacific.com
                </a>
                {" · +386 5 6626 380"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 py-8 px-4">
      <div className="max-w-2xl mx-auto" data-testid="kaizen-survey-page">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-[#1a365d] text-white px-5 py-2 rounded-full mb-4">
            <Sparkles className="h-4 w-4 text-amber-300" />
            <span className="text-xs uppercase tracking-[2px] font-semibold">Kaizen · Continuous Improvement</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-800">Help us improve</h1>
          <p className="text-slate-600 mt-2">
            One minute. Six quick ratings. Shipment{" "}
            <span className="font-medium text-slate-800">{survey.pf_number}</span>{" "}
            handled by{" "}
            <span className="font-medium text-slate-800">{survey.staff_name}</span>.
          </p>
        </div>

        <Card className="shadow-xl">
          <CardHeader className="border-b bg-slate-50/80">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Rate each area</span>
              <span
                data-testid="survey-progress"
                className="text-sm font-normal text-slate-500"
              >
                {filledCount}/6 filled
                {averageRating && (
                  <span className="ml-2 text-amber-600 font-semibold">
                    · avg {averageRating}★
                  </span>
                )}
              </span>
            </CardTitle>
            <CardDescription>
              ⭐ Poor &middot; ⭐⭐ Fair &middot; ⭐⭐⭐ Good &middot; ⭐⭐⭐⭐ Very Good &middot; ⭐⭐⭐⭐⭐ Excellent
            </CardDescription>
          </CardHeader>

          <CardContent className="p-0">
            {CATEGORIES.map((cat, i) => (
              <div
                key={cat.key}
                data-testid={`category-${cat.key}`}
                className={`px-5 sm:px-7 py-5 ${i > 0 ? "border-t" : ""}`}
              >
                <h3 className="text-sm uppercase tracking-wide font-bold text-slate-700">
                  {cat.title}
                </h3>
                <p className="text-sm text-slate-500 mt-0.5 mb-3">{cat.blurb}</p>
                <StarRow
                  value={ratings[cat.key]}
                  onChange={(v) => setRatings((r) => ({ ...r, [cat.key]: v }))}
                  testIdPrefix={cat.key}
                />
              </div>
            ))}

            {/* Improvement */}
            <div className="px-5 sm:px-7 py-6 border-t bg-amber-50/40">
              <Label className="text-sm uppercase tracking-wide font-bold text-slate-700">
                What is the ONE thing we could improve?
              </Label>
              <p className="text-sm text-slate-500 mt-0.5 mb-3">
                Pick the single change that would matter most to your business.
              </p>
              <Textarea
                data-testid="improvement-textarea"
                value={improvement}
                onChange={(e) => setImprovement(e.target.value)}
                placeholder="e.g. faster ETA updates when vessel is delayed, clearer customs status …"
                rows={3}
                className="bg-white"
              />
            </div>

            {/* NPS */}
            <div className="px-5 sm:px-7 py-6 border-t">
              <Label className="text-sm uppercase tracking-wide font-bold text-slate-700">
                Would you recommend Europacific to a business partner?
              </Label>
              <div className="flex gap-3 mt-3">
                <button
                  type="button"
                  data-testid="nps-yes"
                  onClick={() => setWouldRecommend(true)}
                  className={`flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-lg border-2 transition-all ${
                    wouldRecommend === true
                      ? "bg-emerald-500 text-white border-emerald-500 shadow-md"
                      : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300"
                  }`}
                >
                  <ThumbsUp className="h-5 w-5" />
                  <span className="font-semibold">Yes</span>
                </button>
                <button
                  type="button"
                  data-testid="nps-no"
                  onClick={() => setWouldRecommend(false)}
                  className={`flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-lg border-2 transition-all ${
                    wouldRecommend === false
                      ? "bg-rose-500 text-white border-rose-500 shadow-md"
                      : "bg-white text-slate-700 border-slate-200 hover:border-rose-300"
                  }`}
                >
                  <ThumbsDown className="h-5 w-5" />
                  <span className="font-semibold">No</span>
                </button>
              </div>
            </div>

            {/* Submit */}
            <div className="px-5 sm:px-7 py-6 border-t bg-slate-50/80">
              <Button
                data-testid="survey-submit-btn"
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full h-12 text-base bg-[#1a365d] hover:bg-[#0f1e3a]"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  "Submit feedback"
                )}
              </Button>
              <p className="text-xs text-slate-500 text-center mt-3">
                Your responses are confidential and will be used solely to improve our service.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-slate-500">Europacific Logistics d.o.o.</p>
        </div>
      </div>
    </div>
  );
}
